import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { ArrowLeft, Loader2 } from 'lucide-react';
import MaraSportsLogo from '../components/MaraSportsLogo';
import OTPVerification from '../components/OTPVerification';
import LocationPermissionPrompt from '../components/LocationPermissionPrompt';

const AuthPage = () => {
    const [searchParams] = useSearchParams();
    const [isLogin, setIsLogin] = useState(searchParams.get('mode') !== 'register');
    const [loading, setLoading] = useState(false);
    const [showOTPVerification, setShowOTPVerification] = useState(false);
    const [showLocationPrompt, setShowLocationPrompt] = useState(false);
    const [registeredEmail, setRegisteredEmail] = useState('');
    const [capturedLocation, setCapturedLocation] = useState(null);
    const { login, register, refreshUser } = useAuth();
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        email: '',
        password: '',
        fullName: '',
        role: 'athlete'
    });

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // For registration, show location prompt first if not already captured
        if (!isLogin && !capturedLocation && !showLocationPrompt) {
            setShowLocationPrompt(true);
            return;
        }
        
        setLoading(true);

        try {
            if (isLogin) {
                const userData = await login(formData.email, formData.password);
                toast.success('Welcome back!');
                // Redirect admins to admin dashboard
                if (userData.role === 'admin') {
                    navigate('/admin');
                } else {
                    navigate('/dashboard');
                }
            } else {
                const userData = await register(
                    formData.email, 
                    formData.password, 
                    formData.fullName, 
                    formData.role,
                    capturedLocation  // Pass location data
                );
                toast.success('Account created! Please verify your email.');
                setRegisteredEmail(formData.email);
                setShowOTPVerification(true);
            }
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Authentication failed');
        } finally {
            setLoading(false);
        }
    };

    const handleLocationCaptured = (locationData) => {
        setCapturedLocation(locationData);
        setShowLocationPrompt(false);
        // Auto-submit the form after location is captured/skipped
        proceedWithRegistration();
    };

    const proceedWithRegistration = async () => {
        setLoading(true);
        try {
            const userData = await register(
                formData.email, 
                formData.password, 
                formData.fullName, 
                formData.role,
                capturedLocation
            );
            toast.success('Account created! Please verify your email.');
            setRegisteredEmail(formData.email);
            setShowOTPVerification(true);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Registration failed');
        } finally {
            setLoading(false);
        }
    };

    const handleOTPVerified = async () => {
        await refreshUser();
        toast.success('Email verified! Redirecting to profile...');
        navigate('/profile');
    };

    const handleSkipVerification = () => {
        toast.info('You can verify your email later from settings');
        navigate('/profile');
    };

    // Show OTP verification screen after registration
    if (showOTPVerification) {
        return (
            <OTPVerification
                email={registeredEmail}
                onVerified={handleOTPVerified}
                onSkip={handleSkipVerification}
            />
        );
    }

    // Show location permission prompt during registration
    if (showLocationPrompt) {
        return (
            <div className="min-h-screen bg-[#0a1628] flex items-center justify-center p-6">
                <div className="fixed inset-0 grid-lines pointer-events-none" style={{ zIndex: -1 }} />
                <div className="max-w-lg w-full">
                    <LocationPermissionPrompt
                        onLocationCaptured={handleLocationCaptured}
                        context="registration"
                        showSkip={true}
                    />
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#0a1628] flex">
            {/* Grid overlay */}
            <div className="fixed inset-0 grid-lines pointer-events-none" style={{ zIndex: -1 }} />
            
            {/* Left side - Form */}
            <div className="relative z-10 w-full lg:w-1/2 flex flex-col justify-center px-6 md:px-12 lg:px-24 py-12">
                <Link to="/" className="flex items-center gap-2 text-muted-foreground hover:text-white transition-colors mb-12">
                    <ArrowLeft className="w-4 h-4" />
                    <span className="text-sm">Back to Home</span>
                </Link>
                
                <div className="max-w-md w-full mx-auto lg:mx-0">
                    <div className="flex items-center gap-2 mb-8">
                        <MaraSportsLogo className="h-10" />
                        <span className="font-heading font-bold text-2xl tracking-tight uppercase">
                            NextGen<span className="text-primary"> Tournaments</span>
                        </span>
                    </div>
                    
                    <h1 className="font-heading font-bold text-3xl md:text-4xl tracking-tight uppercase mb-2">
                        {isLogin ? 'Welcome Back' : 'Join The Movement'}
                    </h1>
                    <p className="text-muted-foreground mb-8">
                        {isLogin 
                            ? 'Sign in to access your athlete dashboard'
                            : 'Create your account to get started'
                        }
                    </p>
                    
                    <form onSubmit={handleSubmit} className="space-y-6">
                        {!isLogin && (
                            <>
                                <div className="space-y-2">
                                    <Label htmlFor="fullName" className="text-xs tracking-widest uppercase text-muted-foreground">
                                        Full Name
                                    </Label>
                                    <Input
                                        id="fullName"
                                        type="text"
                                        value={formData.fullName}
                                        onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                                        className="bg-[#0d2847] border-white/10 focus:border-primary h-12"
                                        placeholder="Enter your full name"
                                        required
                                        data-testid="register-fullname-input"
                                    />
                                </div>
                                
                                <div className="space-y-2">
                                    <Label htmlFor="role" className="text-xs tracking-widest uppercase text-muted-foreground">
                                        I am a...
                                    </Label>
                                    <Select
                                        value={formData.role}
                                        onValueChange={(value) => setFormData({ ...formData, role: value })}
                                    >
                                        <SelectTrigger className="bg-[#0d2847] border-white/10 h-12" data-testid="register-role-select">
                                            <SelectValue placeholder="Select your role" />
                                        </SelectTrigger>
                                        <SelectContent className="bg-[#0d2847] border-white/10">
                                            <SelectItem value="athlete">Athlete</SelectItem>
                                            <SelectItem value="coach">Coach</SelectItem>
                                            <SelectItem value="scout">Scout / Recruiter</SelectItem>
                                            <SelectItem value="school_admin">School Admin / Head of Sports</SelectItem>
                                            <SelectItem value="organizer">Tournament Organizer</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </>
                        )}
                        
                        <div className="space-y-2">
                            <Label htmlFor="email" className="text-xs tracking-widest uppercase text-muted-foreground">
                                Email Address
                            </Label>
                            <Input
                                id="email"
                                type="email"
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                className="bg-[#0d2847] border-white/10 focus:border-primary h-12"
                                placeholder="you@example.com"
                                required
                                data-testid="auth-email-input"
                            />
                        </div>
                        
                        <div className="space-y-2">
                            <Label htmlFor="password" className="text-xs tracking-widest uppercase text-muted-foreground">
                                Password
                            </Label>
                            <Input
                                id="password"
                                type="password"
                                value={formData.password}
                                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                className="bg-[#0d2847] border-white/10 focus:border-primary h-12"
                                placeholder="••••••••"
                                required
                                minLength={6}
                                data-testid="auth-password-input"
                            />
                        </div>
                        
                        <Button
                            type="submit"
                            disabled={loading}
                            className="w-full bg-primary hover:bg-red-600 text-white border-none btn-glow uppercase tracking-widest text-sm font-bold h-12"
                            data-testid="auth-submit-btn"
                        >
                            {loading ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                                isLogin ? 'Sign In' : 'Create Account'
                            )}
                        </Button>
                    </form>
                    
                    <div className="mt-8 text-center">
                        <p className="text-muted-foreground text-sm">
                            {isLogin ? "Don't have an account?" : "Already have an account?"}
                            <button
                                onClick={() => setIsLogin(!isLogin)}
                                className="text-primary hover:text-red-400 ml-2 font-medium"
                                data-testid="auth-toggle-btn"
                            >
                                {isLogin ? 'Sign Up' : 'Sign In'}
                            </button>
                        </p>
                    </div>
                </div>
            </div>
            
            {/* Right side - Image */}
            <div className="hidden lg:block lg:w-1/2 relative">
                <img 
                    src="https://images.unsplash.com/photo-1553778562-8121e4c332a7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwxfHxhZnJpY2FuJTIweW91dGglMjBwbGF5aW5nJTIwZm9vdGJhbGwlMjBpbnRlbnNlJTIwYWN0aW9ufGVufDB8fHx8MTc3MTk1MzIzNnww&ixlib=rb-4.1.0&q=85"
                    alt="Football match"
                    className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-[#050505] to-transparent" />
                <div className="absolute inset-0 bg-black/40" />
                
                <div className="absolute bottom-0 left-0 right-0 p-12">
                    <div className="bg-black/60 backdrop-blur-lg border border-white/10 p-8">
                        <p className="text-xs tracking-widest uppercase text-primary mb-4">Featured Event</p>
                        <h3 className="font-heading font-bold text-2xl uppercase tracking-tight mb-2">
                            NextGen Tournaments Festival 2026
                        </h3>
                        <p className="text-muted-foreground text-sm mb-4">
                            The premier youth sports event in East Africa. Register now and compete with the best.
                        </p>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                            <span>📍 Nairobi, Kenya</span>
                            <span>📅 July 15-20, 2026</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AuthPage;
